// PROTOTYPE: This file was scaffolded quickly and is pending a rewrite. Do not use as a model for other parts of the codebase.
import {
    AbstractAgent,
    EventType,
    type BaseEvent,
    type RunAgentInput,
} from '@ag-ui/client'
import { Observable } from 'rxjs'

export type AiCreateAgUiEventListener = (event: unknown) => void

export type AiCreateAgUiAgentOptions = {
    sessionId: string
    subscribeEvents: (
        sessionId: string,
        onEvent: AiCreateAgUiEventListener,
        onError?: (event: Event) => void,
    ) => () => void
}

type AgUiWireEvent = {
    type: string
    runId?: string
}

const SSE_RECONNECT_DELAY_MS = 2000

// Reconnects re-emit the hydrate snapshot before replaying the open run;
// forwarding it into a claimed run would corrupt the thread mid-stream (the
// edit-chat agent drops these for the same reason).
const HYDRATE_EVENT_TYPES = new Set<string>([
    'STATE_SNAPSHOT',
    'MESSAGES_SNAPSHOT',
])

function isAgUiWireEvent(value: unknown): value is AgUiWireEvent {
    return (
        !!value &&
        typeof value === 'object' &&
        'type' in value &&
        typeof (value as { type: unknown }).type === 'string'
    )
}

function eventRunId(event: AgUiWireEvent): string | null {
    return typeof event.runId === 'string' && event.runId ? event.runId : null
}

function isTerminalEventType(type: string): boolean {
    return type === EventType.RUN_FINISHED || type === EventType.RUN_ERROR
}

/**
 * Session-scoped AG-UI agent for the create chat. Unlike the edit chat, runs
 * start server-side (the BullMQ job), so `run()` posts nothing: it attaches
 * to the session SSE, drains the events buffered since the run's RUN_STARTED,
 * and forwards live until that run's terminal. `subscribeRunPending` tells
 * the runtime a server run is waiting so it can call `thread.startRun()`.
 */
export class AiCreateAgUiAgent extends AbstractAgent {
    private readonly subscribeEvents: AiCreateAgUiAgentOptions['subscribeEvents']
    private readonly sessionListeners = new Set<AiCreateAgUiEventListener>()
    private readonly runPendingListeners = new Set<() => void>()
    private readonly streamResetListeners = new Set<() => void>()
    private unsubscribeEvents: (() => void) | null = null
    private activeRunTeardown: (() => void) | null = null
    /** Events since a RUN_STARTED that no runtime run() has claimed yet. */
    private pendingRunEvents: AgUiWireEvent[] | null = null
    private closedIntentionally = false
    private reconnectTimeout: ReturnType<typeof setTimeout> | null = null

    constructor(options: AiCreateAgUiAgentOptions) {
        super({ threadId: options.sessionId })
        this.subscribeEvents = options.subscribeEvents
    }

    openSession(): void {
        this.ensureConnected()
    }

    closeSession(): void {
        this.closedIntentionally = true
        if (this.reconnectTimeout) {
            clearTimeout(this.reconnectTimeout)
            this.reconnectTimeout = null
        }
        this.activeRunTeardown?.()
        this.activeRunTeardown = null
        this.unsubscribeEvents?.()
        this.unsubscribeEvents = null
    }

    /** Raw session feed for the activity reducer (panel, preview, errors). */
    subscribeSessionEvents(listener: AiCreateAgUiEventListener): () => void {
        this.sessionListeners.add(listener)
        return () => {
            this.sessionListeners.delete(listener)
        }
    }

    /**
     * Fires before a reconnect resubscribes: the server replays the active
     * run's whole event log on a new connection, so accumulated client state
     * must reset or every replayed note double-applies.
     */
    subscribeStreamReset(listener: () => void): () => void {
        this.streamResetListeners.add(listener)
        return () => {
            this.streamResetListeners.delete(listener)
        }
    }

    /** Fires when a server-side run is buffering with no runtime run to feed. */
    subscribeRunPending(listener: () => void): () => void {
        this.runPendingListeners.add(listener)
        if (this.pendingRunEvents) {
            listener()
        }
        return () => {
            this.runPendingListeners.delete(listener)
        }
    }

    abortRun(): void {
        this.activeRunTeardown?.()
        this.activeRunTeardown = null
        super.abortRun()
    }

    run(_input: RunAgentInput): Observable<BaseEvent> {
        return new Observable<BaseEvent>((observer) => {
            const forwardedEvents = new Set<string>()
            let settled = false
            let activeRunId: string | null = null

            const settle = (error?: unknown): void => {
                if (settled) {
                    return
                }
                settled = true
                this.sessionListeners.delete(dispatch)
                if (this.activeRunTeardown === teardown) {
                    this.activeRunTeardown = null
                }
                if (error !== undefined) {
                    observer.error(error)
                    return
                }
                observer.complete()
            }

            const forward = (event: AgUiWireEvent): void => {
                const key = JSON.stringify(event)
                if (forwardedEvents.has(key)) {
                    return
                }
                forwardedEvents.add(key)
                observer.next(event as BaseEvent)
            }

            const dispatch: AiCreateAgUiEventListener = (raw) => {
                if (settled || !isAgUiWireEvent(raw)) {
                    return
                }
                if (HYDRATE_EVENT_TYPES.has(raw.type)) {
                    return
                }
                const event = raw
                const runId = eventRunId(event)
                if (event.type === EventType.RUN_STARTED) {
                    if (!runId || activeRunId) {
                        return
                    }
                    activeRunId = runId
                    forward(event)
                    return
                }
                if (!activeRunId) {
                    return
                }
                if (runId && runId !== activeRunId) {
                    return
                }
                forward(event)
                if (isTerminalEventType(event.type)) {
                    settle()
                }
            }

            // A torn-down run (redirect, unmount, session switch) completes
            // quietly: the server run is still fine, and erroring here would
            // surface an AbortError to the user on every redirect.
            const teardown = (): void => {
                settle()
            }

            this.activeRunTeardown = teardown

            const buffered = this.pendingRunEvents ?? []
            this.pendingRunEvents = null
            for (const event of buffered) {
                dispatch(event)
            }
            if (!settled) {
                this.sessionListeners.add(dispatch)
            }

            return teardown
        })
    }

    private ensureConnected(): void {
        if (this.unsubscribeEvents) {
            return
        }
        this.closedIntentionally = false
        this.unsubscribeEvents = this.subscribeEvents(
            this.threadId,
            (event) => {
                this.watchForServerRun(event)
                this.sessionListeners.forEach((listener) => listener(event))
            },
            () => {
                this.unsubscribeEvents = null
                if (!this.closedIntentionally) {
                    this.scheduleReconnect()
                }
            },
        )
    }

    /**
     * Buffers a server-initiated run until the runtime claims it: without an
     * attached run() the events would fall on the floor, so RUN_STARTED opens
     * a buffer and pokes the runtime to call `thread.startRun()`.
     */
    private watchForServerRun(raw: unknown): void {
        if (!isAgUiWireEvent(raw) || this.activeRunTeardown) {
            return
        }
        if (HYDRATE_EVENT_TYPES.has(raw.type)) {
            return
        }
        if (raw.type === EventType.RUN_STARTED && eventRunId(raw)) {
            this.pendingRunEvents = [raw]
            this.runPendingListeners.forEach((listener) => listener())
            return
        }
        this.pendingRunEvents?.push(raw)
    }

    private scheduleReconnect(): void {
        if (this.closedIntentionally || this.reconnectTimeout) {
            return
        }
        this.reconnectTimeout = setTimeout(() => {
            this.reconnectTimeout = null
            if (!this.closedIntentionally) {
                this.streamResetListeners.forEach((listener) => listener())
                this.ensureConnected()
            }
        }, SSE_RECONNECT_DELAY_MS)
    }
}
